/**
 * SELECT, MOVE, PLACE — one state machine, three ways in.
 *
 * Tap, keyboard and drag are inputs to this, not three implementations of it. Built once, the
 * keyboard story comes nearly free; built as "drag, plus a fallback", the two drift and only one of
 * them gets tested.
 *
 * It also removes the failure that made a long sort unusable. Dragging across a scroll boundary is
 * where touch drag-and-drop falls apart — not height — so a sort that needs scrolling is merely
 * long once an item can be picked up in one place and put down in another. That is what retires
 * the size guard, and it is why tap-to-place is first-class on every surface rather than a phone
 * affordance.
 *
 * ## Why carrying is a STATE and not a drag
 *
 * A carried item survives anything that happens between picking it up and putting it down —
 * scrolling, changing tab on a phone, tabbing to another bin. A drag cannot survive any of those.
 * The phone layout depends on that directly: you pick up in the Unsorted tab and put down in the
 * Bins tab, and the carry is what crosses between them.
 */
import { closestCenter, pointerWithin, type CollisionDetection } from '@dnd-kit/core';
import { useCallback, useRef, useState } from 'react';

/** Where an unplaced item lives. Not a bin — "not placed" is a state, and the completeness check
 *  the block hangs on depends on telling the two apart. */
export const SOURCE = '__source__';

/** Sections and items share one id space in dnd-kit, so a section's droppable id is prefixed. Bin
 *  ids and item ids are generated apart and would not collide today; this makes that not matter. */
export const zoneId = (id: string) => `zone:${id}`;
export const unzone = (id: string) => (id.startsWith('zone:') ? id.slice(5) : null);

/** Nothing carried, or the one item that is. Deliberately not a list: putting several things in one
 *  bin at once is a different gesture, and allowing it here would make "where does this land"
 *  ambiguous for the keyboard, which has no cursor to land them at. */
export type Carry = string | null;

export interface SortPlacement {
  carrying: Carry;
  /** True while this item is the one in hand — the surfaces use it to mark the row. */
  isCarrying: (itemId: string) => boolean;
  /** Pick up, or put down again if it was already in hand. A second tap on the carried item is
   *  cancel, which is the gesture a student reaches for without being told. */
  toggle: (itemId: string) => void;
  pickUp: (itemId: string) => void;
  cancel: () => void;
  /** Put the carried item into this section. Does nothing with empty hands, so a stray tap on a bin
   *  cannot move whatever was last touched. */
  placeIn: (binId: string, atIndex?: number) => void;
}

/**
 * WHATEVER IS UNDER THE POINTER, and only then whatever is nearest.
 *
 * `closestCenter` compares the DRAGGED RECT'S centre against each droppable's centre, so a bin lit
 * up when the middle of the card crossed into it rather than when the pointer did — and with
 * several narrow columns the nearest centre is often not the column the pointer is in at all, so
 * a bin you were plainly hovering over would refuse to highlight. Both complaints are the same
 * detector.
 *
 * `pointerWithin` answers the question a mouse user is actually asking. It returns nothing when
 * there is no pointer — a keyboard drag, or a pointer outside every droppable — so the rect-based
 * rule stays underneath it as the fallback rather than being replaced.
 *
 * Shared by the editor and the student surface deliberately: they had different detectors, which
 * is two different answers to "where will this land" in one block.
 */
export const sortCollisionDetection: CollisionDetection = (args) => {
  const under = pointerWithin(args);
  if (under.length > 0) return under;

  /*
   * THE GAP BETWEEN TWO BINS BELONGS TO WHICHEVER IS NEARER THE POINTER.
   *
   * `pointerWithin` claims nothing there, and falling straight through to `closestCenter` measured
   * the DRAGGED RECT instead — so releasing in the few pixels between two columns consistently
   * dropped into the last bin rather than the one being aimed at. This measures the pointer's own
   * distance to each droppable rectangle, which is zero inside it and grows with the gap, so the
   * nearer edge wins and the behaviour matches what the cursor is doing.
   */
  const p = args.pointerCoordinates;
  if (p) {
    const ranked = args.droppableContainers
      .map((container) => {
        const rect = args.droppableRects.get(container.id);
        if (!rect) return null;
        const dx = Math.max(rect.left - p.x, 0, p.x - (rect.left + rect.width));
        const dy = Math.max(rect.top - p.y, 0, p.y - (rect.top + rect.height));
        return { id: container.id, data: { droppableContainer: container, value: Math.hypot(dx, dy) } };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .sort((a, b) => a.data.value - b.data.value);
    if (ranked.length > 0) return ranked;
  }

  /* No pointer at all — a keyboard drag. The rect rule is the only one that can answer. */
  return closestCenter(args);
};

export function useSortPlacement(onPlace: (itemId: string, binId: string, atIndex?: number) => void): SortPlacement {
  const [carrying, setCarrying] = useState<Carry>(null);
  /**
   * THE CARRY TWICE: a ref the handlers read and write, and state for the render.
   *
   * Both properties below are required, and either one alone is easy:
   *
   * 1. PLACE EXACTLY ONCE per carry. A double-tap or a stray second event must not file the item
   *    twice — on a sort that silently moves it somewhere the student did not choose. Two presses
   *    in one tick both see the same `carrying` STATE, because no re-render has happened between
   *    them, so a state read cannot be the guard.
   * 2. NO STATE UPDATE DURING RENDER. `onPlace` ends in the owning block's `setTestPlacements`.
   *
   * This used to satisfy (1) by calling `onPlace` inside a `setCarrying` updater, which reads and
   * clears the carry in one atomic step. That broke (2): React runs updater functions during the
   * RENDER phase, so every tap logged "Cannot update a component (`SortBlock`) while rendering a
   * different component (`SortAnswerDisplay`)" and wrote into a render React may discard and redo.
   *
   * The ref is the atomic part, cleared before `onPlace` is called, so the second press in the
   * same tick reads `null` and returns. It is mutated only in EVENT HANDLERS, never during
   * render, which is what separates it from the ref hazard that rule is about — a ref written
   * while rendering survives a render that gets thrown away; one written from an event does not.
   *
   * Every setter writes both, so the two cannot disagree. `setCarry` is the only way to do it.
   */
  const carryingRef = useRef<Carry>(null);
  const setCarry = useCallback((next: Carry) => {
    carryingRef.current = next;
    setCarrying(next);
  }, []);

  const placeIn = useCallback((binId: string, atIndex?: number) => {
    const held = carryingRef.current;
    if (held === null) return;
    setCarry(null);
    onPlace(held, binId, atIndex);
  }, [onPlace, setCarry]);

  return {
    carrying,
    isCarrying: useCallback((itemId: string) => carrying === itemId, [carrying]),
    toggle: useCallback((itemId: string) => setCarry(carryingRef.current === itemId ? null : itemId), [setCarry]),
    pickUp: useCallback((itemId: string) => setCarry(itemId), [setCarry]),
    cancel: useCallback(() => setCarry(null), [setCarry]),
    placeIn,
  };
}

/**
 * What a listener is told, as the carry moves.
 *
 * A sort's announcements name the BIN, where a list's name a position: "Moved Granite to Igneous"
 * is the fact, and "item 3 of 7" is not — within-bin order carries no meaning unless the teacher
 * turned it on, so announcing a position would be announcing something that does not count.
 *
 * Returned as strings rather than spoken here, so the surfaces can put them in whichever live
 * region they already own.
 */
export function placementAnnouncements(
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  return {
    pickedUp: (item: string) => t('blocks.sort.a11y.pickedUp', { item }),
    placed: (item: string, bin: string) => t('blocks.sort.a11y.placed', { item, bin }),
    returned: (item: string) => t('blocks.sort.a11y.returned', { item }),
    cancelled: (item: string) => t('blocks.sort.a11y.cancelled', { item }),
  };
}

/**
 * WHERE A DROP LANDS — the one piece of drag arithmetic worth pinning.
 *
 * Returns null for a drag that ends on itself or on nothing. The caller treats that as "still in
 * hand" rather than as a move: the student has already said which item they mean, so the recovery
 * is to put it down somewhere, not to start again.
 *
 * The index is counted against the destination list WITHOUT the dragged item in it, because a
 * placement removes the item everywhere before splicing it back. Count against the list as
 * rendered and a move further down its own bin lands one position short — the classic off-by-one
 * that looks like a rendering glitch and is not.
 */
export function resolveDrop(
  overId: string,
  draggedId: string,
  ctx: { binIds: string[]; placements: Record<string, string[]>; isItem: (id: string) => boolean },
): { binId: string; atIndex?: number } | null {
  const zone = unzone(overId);
  if (zone !== null) return zone === SOURCE || ctx.binIds.includes(zone) ? { binId: zone } : null;
  if (overId === draggedId || !ctx.isItem(overId)) return null;
  const destination = ctx.binIds.find(binId => (ctx.placements[binId] ?? []).includes(overId));
  if (destination === undefined) return { binId: SOURCE };
  const without = (ctx.placements[destination] ?? []).filter(id => id !== draggedId);
  return { binId: destination, atIndex: Math.max(0, without.indexOf(overId)) };
}
